import { Button, Checkbox, Divider, Grid, Group, Modal, Tabs, Title } from '@mantine/core';
import { IconDeviceFloppy, IconLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import additionalActions from '../../constants/additionalSubscriptionsActions';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';

const SubscriptionPermissionsManager = ({
  subscriptions,
  selectedSubscriptions,
  setSelectedSubscriptions,
  accountId,
  domainId,
}) => {
  const fetch = useFetch();
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSubscriptionsInner, setSelectedSubscriptionsInner] = useState(selectedSubscriptions);

  const actionSet = new Set(additionalActions);
  subscriptions.forEach((subscription) => {
    subscription.allowedActions.forEach((a) => actionSet.add(a));
  });

  const allPossibleActions = [...actionSet].sort().map((action) => ({
    value: action,
    label: action === '*' ? '[FULL ACCESS]' : prettifiedLabel(action),
  }));

  useEffect(() => {
    setSelectedSubscriptionsInner(selectedSubscriptions);
  }, [selectedSubscriptions]);

  const setSubscription = (i, subscription) => {
    const updatedSubscriptions = JSON.parse(JSON.stringify(selectedSubscriptionsInner));
    updatedSubscriptions[i] = { ...updatedSubscriptions[i], ...subscription };
    setSelectedSubscriptionsInner(updatedSubscriptions);
  };

  const savePermissions = async () => {
    if (accountId && domainId) {
      setSaving(true);
      try {
        await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/subscriptions/permissions`, {
          method: 'PATCH',
          body: JSON.stringify({ selectedSubscriptions: selectedSubscriptionsInner }),
        });
        setSelectedSubscriptions(selectedSubscriptionsInner);
        setModalOpen(false);
      } finally {
        setSaving(false);
      }
    } else {
      setSelectedSubscriptions(selectedSubscriptionsInner);
      setModalOpen(false);
    }
  };

  return (
    <>
      <Button variant="default" disabled={selectedSubscriptions.length === 0} onClick={() => setModalOpen(true)}>
        <IconLock size={16} /> Permissions
      </Button>

      <Modal size="full" opened={modalOpen} onClose={() => setModalOpen(false)} title="Manage Subscription Permissions">
        <form>
          <Tabs defaultValue="0">
            <Tabs.List mb="md">
              {selectedSubscriptionsInner.map((sub, i) => (
                <Tabs.Tab key={i} value={i.toString()}>
                  {sub.name}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {selectedSubscriptionsInner.map((sub, i) => (
              <Tabs.Panel key={i} value={i.toString()}>
                <div style={{ paddingBottom: 'var(--mantine-spacing-xl)' }}>
                  <Title order={5} pb="md">
                    Permissions for {sub.name}
                  </Title>
                  <Divider my="sm" />
                  <Grid>
                    {allPossibleActions.map(({ value, label }) => (
                      <Grid.Col key={value} span={{ base: 12, md: 3 }}>
                        <Checkbox
                          display="inline"
                          mr="sm"
                          name={value}
                          disabled={sub.allowedActions.includes('*') && value !== '*'}
                          checked={sub.allowedActions.includes(value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (e.target.name === '*') {
                                setSubscription(i, { allowedActions: ['*'] });
                              } else {
                                setSubscription(i, {
                                  allowedActions: [...sub.allowedActions, e.target.name].sort(),
                                });
                              }
                            } else {
                              setSubscription(i, {
                                allowedActions: sub.allowedActions.filter((a) => a !== value),
                              });
                            }
                          }}
                          label={label}
                        />
                      </Grid.Col>
                    ))}
                  </Grid>
                </div>
              </Tabs.Panel>
            ))}
          </Tabs>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button loading={saving} onClick={savePermissions}>
              Save <IconDeviceFloppy size={16} />
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
};

export default SubscriptionPermissionsManager;
